import { HardDrive } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  diskRateScale,
  peakDiskRate,
  useDiskRateUnit,
} from "@/hooks/useDiskRateUnit";
import {
  CHART_GRID_STROKE,
  CHART_GRID_STROKE_OPACITY,
  CHART_TICK_FILL,
  chartTooltipContentStyle,
  formatMetricsTimestamp,
} from "@/lib/chartTheme";
import type { ResourceMetricsSeries } from "@/types/admin";

interface ResourceDiskIoCardProps {
  metrics?: ResourceMetricsSeries;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** Asks for the series again from the error state. */
  onRetry?: () => void;
  /** e.g. "1H" / "24H" — drives the X-axis timestamp granularity and the card's subtitle. */
  rangeLabel: string;

  /**
   * Why there is nothing to plot, when the resource's state means metrics are never fetched at
   * all. Without it a Provisioning VM would read as "no throughput" rather than "not yet".
   */
  unavailableNote?: string;
}

/**
 * Disk read/write throughput for the Storage tab, mirroring the client app's
 * `ResourceStorageTab` I/O card: one line per direction plus an averages/peak footer.
 *
 * The card renders in every state. It used to be wrapped in `metrics.diskRead.length > 0`, so a
 * VM that was still loading, or whose metrics call had failed, simply had no Disk I/O card at all
 * — indistinguishable from a VM with no throughput.
 *
 * Values arrive in MB/s (the service converts the backend's bytes/sec, as the client does); the axis,
 * tooltip and footer show KB/s or MB/s by the same rule as the Metrics tab (useDiskRateUnit).
 */
export function ResourceDiskIoCard({
  metrics,
  isLoading,
  isFetching,
  error,
  onRetry,
  rangeLabel,
  unavailableNote,
}: ResourceDiskIoCardProps) {
  const readPoints = metrics?.disk.read ?? [];
  const writePoints = metrics?.disk.write ?? [];

  // KB/s or MB/s, same rule as the Metrics tab (useDiskRateUnit). A fetch in flight is a poll or a
  // range switch; only the settled series moves the held unit.
  const diskUnit = useDiskRateUnit(
    peakDiskRate(readPoints, writePoints),
    !!metrics && !isFetching,
  );
  const diskScale = diskRateScale(diskUnit);

  const reads = readPoints.map((point) => point.value * diskScale);
  const writes = writePoints.map((point) => point.value * diskScale);

  // Paired by index with the read series, as the client card does.
  const chartData = readPoints.map((point, i) => ({
    time: formatMetricsTimestamp(point.timestamp, rangeLabel),
    read: point.value * diskScale,
    write: writes[i] ?? 0,
  }));

  const stats = !unavailableNote && reads.length
    ? {
        avgRead: reads.reduce((sum, v) => sum + v, 0) / reads.length,
        avgWrite: writes.length
          ? writes.reduce((sum, v) => sum + v, 0) / writes.length
          : 0,
        // Peak of the combined rate, not of either direction alone — that is the number that
        // tells an operator how close the volume came to saturating.
        peak: reads.reduce(
          (max, read, i) => Math.max(max, read + (writes[i] ?? 0)),
          0,
        ),
      }
    : null;

  return (
    <Card className="border-border/80 shadow-none">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-4 w-4" />
          Disk I/O
        </CardTitle>
        <CardDescription>
          Read and write throughput over the last {rangeLabel}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {unavailableNote ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {unavailableNote}
          </p>
        ) : isLoading && !metrics ? (
          <Skeleton className="h-[180px] w-full rounded-lg" />
        ) : error && !metrics ? (
          // Only with nothing to draw: the series polls, and a failed poll keeps the last samples.
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-destructive">
              {error instanceof Error
                ? error.message
                : "Failed to load disk metrics."}
            </p>
            {onRetry && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onRetry}
                disabled={isFetching}
              >
                {isFetching && <Spinner className="mr-2" />}
                Retry
              </Button>
            )}
          </div>
        ) : chartData.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No disk throughput samples for this range.
          </p>
        ) : (
          <div className="relative">
            {/* Hidden, as on the client Storage tab: it blinked on every 60s background poll over a
                chart that redraws in place. To restore, uncomment.
            {isFetching && (
              <div className="pointer-events-none absolute right-0 top-0 z-10 text-[10px] text-muted-foreground">
                Updating…
              </div>
            )} */}
            <ResponsiveContainer width="100%" height={180}>
              <LineChart
                data={chartData}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11, fill: CHART_TICK_FILL }}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <YAxis
                  width={36}
                  tick={{ fontSize: 11, fill: CHART_TICK_FILL }}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                  tickFormatter={(v: number) =>
                    v < 1 ? v.toFixed(2) : v.toFixed(1)
                  }
                />
                <Tooltip
                  itemSorter="dataKey"
                  contentStyle={chartTooltipContentStyle}
                  formatter={(value, name) => [
                    `${Number(value).toFixed(2)} ${diskUnit}`,
                    name,
                  ]}
                />
                {/* No animation: each poll slides the window one sample, and the default 1.5s tween moved
                    every point to its neighbour's value, so the whole line rippled once a minute. */}
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="read"
                  name="Read"
                  stroke="var(--info)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="write"
                  name="Write"
                  stroke="var(--brand-hover)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
      {stats && (
        <CardFooter className="flex flex-wrap gap-x-6 gap-y-1 border-t border-border/60 pt-4 font-mono text-[11px] text-muted-foreground">
          <span>
            Read avg{" "}
            <b className="tabular-nums text-foreground">
              {stats.avgRead.toFixed(2)} {diskUnit}
            </b>
          </span>
          <span>
            Write avg{" "}
            <b className="tabular-nums text-foreground">
              {stats.avgWrite.toFixed(2)} {diskUnit}
            </b>
          </span>
          <span className="ml-auto min-w-0">
            Peak (read+write){" "}
            <b className="tabular-nums text-foreground">
              {stats.peak.toFixed(2)} {diskUnit}
            </b>
          </span>
        </CardFooter>
      )}
    </Card>
  );
}
